import { Timestamp } from 'firebase/firestore';
import moment from 'moment';

import { FSCollections } from '@src/constants/database';
import WorkJourneyConfigMapper from '@src/models/mappers/WorkJourneyConfigMapper';
import { getUserListStatus } from '@src/packages/users/screens/UsersList/utils/getUserListStatus';
import { userHasAttendanceAddress } from '@src/packages/users/screens/UsersList/utils/userHasAttendanceAddress';
import { WORK_JOURNEY_LIST_SEARCH_IDS_SIZE } from '@src/packages/workJourney/constants';
import {
  refreshWorkJourneyAssignedUsers,
  refreshWorkJourneyMembersAndSupervisors,
} from '@src/packages/workJourney/utils/refreshWorkJourneyAssignedUsers';
import { WorkJourneyValidationError } from '@src/packages/workJourney/utils/submitWorkJourneyForm';
import { validateWorkJourneyForm } from '@src/packages/workJourney/utils/validateWorkJourneyForm';
import safeExecute from '@src/util/safeExecute';

import UserRepository from '../repositories/UserRepository';
import WorkJourneyRepository from '../repositories/WorkJourneyRepository';
import { sendErrorToSentry } from '../util/sentry/utils';

class WorkJourneyService {
  static MAX_WORK_HOURS_PER_DAY = 12;
  static MAX_WORK_HOURS_PER_WEEK = 48;
  static MIN_BREAK_TIME = 30; // minutes

  constructor() {
    this.repository = new WorkJourneyRepository();
    this.userRepository = new UserRepository();
  }

  /** Live org-user fetch used before member asserts on create/update/sync. */
  getOrganizationUser = (organizationId, userId) =>
    this.userRepository.getUser(organizationId, userId);

  /** Helper functions */

  /**
   * @description Convert Moment to Timestamp
   * @param {Moment} momentObj - The moment to convert
   * @returns {Timestamp} - The converted timestamp
   */
  static convertMomentToTimestamp(momentObj) {
    if (!momentObj) return null;
    return Timestamp.fromMillis(momentObj.valueOf());
  }

  /**
   * @description Normalize any date-like value to Firestore Timestamp
   * @param {any} value - The value to normalize
   * @returns {Timestamp|any} - Timestamp when conversion is possible, otherwise original value
   */
  static normalizeToTimestamp(value) {
    if (!value) return value;

    if (value instanceof Timestamp) return value;

    if (moment.isMoment(value)) {
      return WorkJourneyService.convertMomentToTimestamp(value);
    }

    if (value instanceof Date) {
      return Timestamp.fromDate(value);
    }

    const coerceToInteger = raw => {
      if (Number.isInteger(raw)) return raw;
      if (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))) {
        return Math.trunc(Number(raw));
      }
      return null;
    };

    if (typeof value === 'object') {
      const seconds = coerceToInteger(value.seconds ?? value._seconds ?? value.secodns);
      const nanoseconds = coerceToInteger(value.nanoseconds ?? value._nanoseconds ?? 0);

      if (seconds !== null && nanoseconds !== null) {
        return new Timestamp(seconds, nanoseconds);
      }
    }

    return value;
  }


  /**
   * @description Convert multiple moment fields to timestamps in an object
   * This function is useful for handling nested object structures where moment objects
   * need to be converted to Firestore Timestamps before saving to the database.
   *
   * @param {Object} data - The object containing moment fields
   * @param {Array<string>} fieldPaths - Array of field paths to convert (e.g., ['field', 'nested.field'])
   * @returns {Object} - The object with converted timestamp fields
   *
   * @example
   * // Convert single level fields
   * convertMomentFieldsToTimestamp(data, ['startDate', 'endDate'])
   *
   * // Convert nested fields
   * convertMomentFieldsToTimestamp(data, ['workShift.createdAt', 'schedule.startTime'])
   */
  static convertMomentFieldsToTimestamp(data, fieldPaths) {
    if (!data || !Array.isArray(fieldPaths)) return data;

    const result = { ...data };

    fieldPaths.forEach(fieldPath => {
      const fieldParts = fieldPath.split('.');
      let current = result;

      for (let i = 0; i < fieldParts.length - 1; i++) {
        if (!current[fieldParts[i]]) return;
        current = current[fieldParts[i]];
      }

      const finalKey = fieldParts[fieldParts.length - 1];

      if (current && current[finalKey]) {
        current[finalKey] = WorkJourneyService.normalizeToTimestamp(current[finalKey]);
      }
    });

    return result;
  }

  /**
   * @description Convert Timestamp to Moment
   * @param {Timestamp} timestamp - The timestamp to convert
   * @returns {Moment} - The converted moment
   */
  static convertTimestampToMoment(timestamp) {
    if (!timestamp) return null;
    if (timestamp?.seconds) {
      return moment(timestamp.seconds * 1000);
    }
    return moment(timestamp.toMillis());
  }

  /**
   * @description Format minutes to HH:MM
   * @param {number} totalMinutes - The total minutes
   * @returns {string} - The formatted time
   */
  static formatMinutesToHHMM(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  /**
   * @description Clean time fields from moment objects to HH:mm:ss format
   * @param {Object} workJourneyData - The work journey data to clean
   * @returns {Object} - The cleaned work journey data
   */
  static cleanTimeFields(workJourneyData) {
    let cleanedData = { ...workJourneyData };
    const timeFields = ['breakStartTime', 'breakEndTime'];

    timeFields.forEach(field => {
      if (cleanedData[field] && moment.isMoment(cleanedData[field])) {
        cleanedData[field] = cleanedData[field].format('HH:mm');
      }
    });

    // Fields that need to be converted from moment to Timestamp
    const dateFields = [
      'shiftStartDate',
      'shiftEndDate',
      'startDate',
      'endDate',
      'createdAt',
      'updatedAt',
    ];

    dateFields.forEach(field => {
      if (cleanedData[field]) {
        cleanedData[field] = WorkJourneyService.normalizeToTimestamp(cleanedData[field]);
      }
    });

    const nestedMomentFields = [
      'address.createdAt',
      'address.updatedAt',
      'workShift.createdAt',
      'workShift.updatedAt',
      'createdAt',
      'updatedAt',
    ];
    cleanedData = WorkJourneyService.convertMomentFieldsToTimestamp(
      cleanedData,
      nestedMomentFields,
    );

    // Convert recurrence week days form moment object to string with the following format '00:00'
    try {
      cleanedData.workShift.recurrence.weeks.forEach(week => {
        Object.values(week.days).forEach(day => {
          if (day.startBreakTime) {
            day.startBreakTime = day.startBreakTime?._i || day.startBreakTime;
          }
          if (day.endBreakTime) {
            day.endBreakTime = day.endBreakTime?._i || day.endBreakTime;
          }
          if (day.startTime) {
            day.startTime = day.startTime?._i || day.startTime;
          }
          if (day.endTime) {
            day.endTime = day.endTime?._i || day.endTime;
          }
        });
      });
    } catch (error) {
      sendErrorToSentry(error, { functionName: 'cleanTimeFields' });
    }

    return cleanedData;
  }

  /**
   * @description Calculate work hours between two times
   * @param {string} startTime - The start time
   * @param {string} endTime - The end time
   * @param {number} breakTime - The break time
   * @returns {Object} - An object containing the work hours
   */
  static calculateWorkHours(startTime, endTime, breakTime = 0) {
    if (!startTime || !endTime) {
      return {
        minutes: 0,
        hours: 0,
        formatted: '00:00',
      };
    }

    const start = moment(startTime, 'HH:mm');
    const end = moment(endTime, 'HH:mm');
    if (end.isBefore(start)) end.add(1, 'day');

    const workMinutes = end.diff(start, 'minutes') - breakTime;
    const workHours = parseFloat((workMinutes / 60).toFixed(2));

    return {
      minutes: workMinutes,
      hours: workHours,
      formatted: this.formatMinutesToHHMM(workMinutes),
    };
  }

  /**
   * @description Validate work journey data
   * @param {WorkJourney} workJourneyData - The work journey data to validate
   * @param {Object} translations - Object containing translation keys for error messages
   * @returns {Object} - An object containing the validation result
   */
  static validateWorkJourney(workJourneyData, translations = {}) {
    const t = {
      nameRequired: 'El nombre de la jornada es requerido',
      workShiftRequired: 'El turno es requerido',
      workShiftIdRequired: 'El turno seleccionado no es válido',
      startDateRequired: 'La fecha de inicio es requerida',
      supervisorsRequired: 'Supervisores son requeridos',
      invalidRequiredMembers: 'La cantidad de usuarios asignados a la jornada no es válida',
      locationRequired: 'La ubicación es requerida',
      locationTypeRequired: 'El tipo de ubicación es requerido',
      breakMarkingTypeRequired: 'El tipo de marcación de colación es requerido',
      breakStartTimeRequired: 'La hora de inicio de colación es requerida',
      toleranceTimeInvalid: 'La tolerancia debe ser un número entre 0 y 120 minutos',
      earlyEndTimeInvalid: 'La terminación anticipada debe ser un número entre 0 y 120 minutos.',
      shiftEndDateBeforeStart: 'La fecha de término no puede ser anterior a la fecha de inicio',
      journeyTypeRequired: 'El tipo de jornada es requerido',
      workCircumstancesRequired: 'Las circunstancias de trabajo son requeridas',
      ...translations,
    };

    return validateWorkJourneyForm(workJourneyData, t);
  }

  static assertMembersHaveAttendanceAddress(members = [], translations = {}) {
    const incomplete = (members || []).filter(
      member => !userHasAttendanceAddress(member?.user?.address),
    );
    if (incomplete.length) {
      const userIds = incomplete
        .map(member => member?.user?.id || member?.id)
        .filter(Boolean);
      const displayNames = incomplete
        .map(member => {
          const user = member?.user;
          if (!user) return '';
          const fullName = [user.firstName, user.lastName]
            .filter(part => typeof part === 'string' && part.trim())
            .join(' ')
            .trim();
          return fullName || user.displayName || user.email || user.id || '';
        })
        .filter(Boolean);
      const error = new Error(
        translations.membersMissingAddress ||
          'Users without an address cannot be assigned to a work journey',
      );
      error.name = 'AttendanceAddressRequiredError';
      error.code = 'attendance-address-required';
      error.userIds = userIds;
      error.displayNames = displayNames;
      throw error;
    }
  }

  static assertMembersAreActive(members = [], translations = {}) {
    const getMemberDisplayName = member => {
      const user = member?.user;
      if (!user) return '';
      const fullName = [user.firstName, user.lastName]
        .filter(part => typeof part === 'string' && part.trim())
        .join(' ')
        .trim();
      return fullName || user.displayName || user.email || user.id || '';
    };

    // Only explicit `onboardingCompleted === false` is pending (undefined = legacy active).
    const pending = (members || []).filter(
      member => member?.user?.onboardingCompleted === false,
    );
    if (pending.length) {
      const error = new Error(
        translations.membersPending ||
          'Pending users cannot be assigned to a work journey',
      );
      error.name = 'MembersPendingError';
      error.code = 'members-pending';
      error.userIds = pending
        .map(member => member?.user?.id || member?.id)
        .filter(Boolean);
      error.displayNames = pending.map(getMemberDisplayName).filter(Boolean);
      throw error;
    }

    const inactive = (members || []).filter(
      member => getUserListStatus(member?.user ?? {}) === 'inactive',
    );
    if (inactive.length) {
      const error = new Error(
        translations.membersInactive ||
          'Inactive users cannot be assigned to a work journey',
      );
      error.name = 'MembersInactiveError';
      error.code = 'members-inactive';
      error.userIds = inactive
        .map(member => member?.user?.id || member?.id)
        .filter(Boolean);
      error.displayNames = inactive.map(getMemberDisplayName).filter(Boolean);
      throw error;
    }
  }

  /**
   * @description Create work journey
   * @param {WorkJourney} workJourneyData - The work journey data to create
   * @param {string} organizationId - The ID of the organization
   * @param {Object} translations - Object containing translation keys for error messages
   * @returns {Promise<void>}
   */
  async create(workJourneyData, organizationId, translations = {}) {
    const cleanedData = WorkJourneyService.cleanTimeFields(workJourneyData);
    const refreshedUsers = await refreshWorkJourneyMembersAndSupervisors(
      organizationId,
      cleanedData,
      this.getOrganizationUser,
    );
    cleanedData.members = refreshedUsers.members;
    cleanedData.supervisors = refreshedUsers.supervisors;
    const validation = WorkJourneyService.validateWorkJourney(cleanedData, translations);
    if (!validation.isValid) throw new WorkJourneyValidationError(validation.errors);
    WorkJourneyService.assertMembersHaveAttendanceAddress(cleanedData.members, translations);
    WorkJourneyService.assertMembersAreActive(cleanedData.members, translations);

    const workJourney = {
      ...cleanedData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      organizationId,
    };

    return this.repository.create(workJourney, organizationId);
  }

  /**
   * @description Update work journey
   * @param {WorkJourney} workJourneyData - The work journey data to update
   * @param {string} organizationId - The ID of the organization
   * @param {Object} translations - Object containing translation keys for error messages
   * @returns {Promise<void>}
   */
  async update(workJourneyData, organizationId, translations = {}) {
    const cleanedData = WorkJourneyService.cleanTimeFields(workJourneyData);
    const refreshedUsers = await refreshWorkJourneyMembersAndSupervisors(
      organizationId,
      cleanedData,
      this.getOrganizationUser,
    );
    cleanedData.members = refreshedUsers.members;
    cleanedData.supervisors = refreshedUsers.supervisors;
    const validation = WorkJourneyService.validateWorkJourney(cleanedData, translations);
    if (!validation.isValid) throw new WorkJourneyValidationError(validation.errors);
    if (cleanedData.members) {
      WorkJourneyService.assertMembersHaveAttendanceAddress(cleanedData.members, translations);
      WorkJourneyService.assertMembersAreActive(cleanedData.members, translations);
    }
    const workJourney = { ...cleanedData, organizationId, updatedAt: Timestamp.now() };
    return this.repository.update(workJourney, organizationId);
  }

  /**
   * @description Delete work journey
   * @param {string} workJourneyId - The ID of the work journey
   * @param {string} organizationId - The ID of the organization
   * @returns {Promise<void>}
   */
  async delete(workJourneyId, organizationId) {
    return this.repository.delete(workJourneyId, organizationId);
  }

  /**
   * @description List work journey configs for an organization
   * @param {string} organizationId - The ID of the organization
   * @returns {Promise<Array<{ id: string, name: string }>>}
   */
  async listConfigs(organizationId) {
    const configs = await this.repository.listConfigs(organizationId);

    return (configs || [])
      .filter(config => config?.id)
      .map(config => ({
        id: config.id,
        name: (config.name || config.journeyName || '').trim() || config.id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }

  /**
   * Resolves work journey config document ids matching list text search in Elastic.
   * Used as the ID source for Firestore `documentId in` realtime queries.
   */
  async searchIds({
    orgId,
    searchTerm = '',
    pageSize = WORK_JOURNEY_LIST_SEARCH_IDS_SIZE,
    from = 0,
    exactMatch = false,
  }) {
    return safeExecute(
      async () => {
        if (!orgId) {
          throw new Error('Organization ID is required');
        }

        const filters = WorkJourneyConfigMapper.toListElasticFilters({
          searchTerm,
          exactMatch,
        });

        const params = {
          entity: FSCollections.WORK_JOURNEY_CONFIGS,
          organizationId: orgId,
          filters,
          pagination: { from, size: pageSize },
          sort: [{ field: 'createdAt._seconds', order: 'desc' }],
          fields: ['id'],
        };

        const response = await this.repository.queryElastic(params);
        return WorkJourneyConfigMapper.fromElasticSearchIdsResponse(response);
      },
      'WorkJourneyService.searchIds',
      { orgId, pageSize, from },
    );
  }

  /**
   * Resolves work journey config ids for journeys that include any of the given users as members.
   */
  async listConfigIdsByMemberUserIds(organizationId, userIds) {
    return safeExecute(
      async () => {
        if (!organizationId) {
          throw new Error('Organization ID is required');
        }

        return this.repository.listConfigIdsByMemberUserIds(organizationId, userIds ?? []);
      },
      'WorkJourneyService.listConfigIdsByMemberUserIds',
      { organizationId, userCount: userIds?.length ?? 0 },
    );
  }

  /**
   * @description List assigned members for a work journey config
   * @param {string} workJourneyId - The ID of the work journey config
   * @param {string} organizationId - The ID of the organization
   * @returns {Promise<Array>} - Assigned member documents
   */
  async getMembers(workJourneyId, organizationId) {
    return safeExecute(
      () => this.repository.getMembers(organizationId, workJourneyId),
      'WorkJourneyService.getMembers',
      { organizationId, workJourneyId },
    );
  }

  /**
   * @description Sync work journey members by comparing existing with new ones
   * @param {string} workJourneyId - The ID of the work journey
   * @param {string} organizationId - The ID of the organization
   * @param {Array} newMembers - Array of new members to sync
   * @returns {Promise<Object>} - Result of the sync operation
   */
  async updateMembers(workJourneyId, organizationId, newMembers, translations = {}) {
    const members = await refreshWorkJourneyAssignedUsers(
      organizationId,
      newMembers,
      this.getOrganizationUser,
    );
    WorkJourneyService.assertMembersHaveAttendanceAddress(members, translations);
    WorkJourneyService.assertMembersAreActive(members, translations);
    return this.repository.updateMembers(workJourneyId, organizationId, members);
  }
}

export default WorkJourneyService;
